import { useQuery } from '@tanstack/react-query'
import { baseURL, headers, STALE_TIME } from '@/v2/constants/requests'

const EMPTY_SUMMARY = {
  paymentCount: 0,
  totalCollected: '0.0',
  patientFees: '0.0',
  achVolume: '0.0',
  achCount: 0,
  ccVolume: '0.0',
  waivedCount: 0,
  achShare: 0,
}

const EMPTY_PAGINATION = {
  currentPage: 1,
  perPage: 25,
  totalPages: 0,
  totalRecords: 0,
}

// Backs the billing dashboard. Filtering, pagination and the summary
// aggregates are all computed server-side, so this hook is a thin wrapper
// around GET /v2/rest/reports/payments_dashboard — the response shape is the
// runtime contract documented in
// docs/v2/private/rest/reports/payments_dashboard_doc.md.
export const usePaymentsDashboard = (
  { startDate, endDate, method, paymentStatus, search } = {},
  { page = 1, perPage = 25 } = {}
) => {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      'paymentsDashboard',
      startDate,
      endDate,
      method,
      paymentStatus,
      search,
      page,
      perPage,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        page,
        per_page: perPage,
      })
      if (method) params.append('payment_method', method)
      if (paymentStatus) params.append('payment_status', paymentStatus)
      if (search) params.append('search', search)

      const res = await fetch(
        `${baseURL}/v2/rest/reports/payments_dashboard?${params}`,
        {
          method: 'GET',
          headers,
        }
      )

      if (!res.ok) {
        if (res.status === 403) {
          throw new Error('Admin access required to view the billing dashboard')
        }
        const body = await res.json().catch(() => null)
        throw new Error(
          body?.errors?.[0] || 'Failed to load the billing dashboard'
        )
      }

      return res.json()
    },
    staleTime: STALE_TIME,
    cacheTime: STALE_TIME * 2,
    enabled: !!startDate && !!endDate,
    keepPreviousData: true,
    retry: 1,
  })

  return {
    summary: data?.summary || EMPTY_SUMMARY,
    payments: data?.payments?.data || [],
    pagination: data?.payments?.pagination || EMPTY_PAGINATION,
    isLoading,
    isError,
    error,
    refetch,
  }
}
